import React, { useRef, useState, useEffect, useCallback } from "react";
import { IconButton } from "@mui/material";
import { MdCheck } from "react-icons/md";
import WaveSurfer from "wavesurfer";

function AudioItem({ item, setItem, editing, leftPadding, index, lang }) {
  const sourceTextRef = useRef();
  const optionsWindowRef = useRef();
  const wsRef = useRef();
  const wsContainer = useRef();
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => {
      wsRef.current?.destroy();
    };
  }, []);

  useEffect(() => {
    if (wsRef.current) wsRef.current.drawBuffer(); // not firing somehow??
  }, [index, item, editing]);

  useEffect(() => {
    if (wsContainer.current && item?.content[lang.code] && !wsRef.current) {
      setLoading(true);

      wsRef.current = WaveSurfer.create({
        container: wsContainer.current,
        backend: "MediaElement",
        waveColor: "#c6dce9",
        progressColor: "#4677a9",
        normalize: true,
        height: 40,
        removeMediaElementOnDestroy: true,
      });

      wsRef.current.load(item.content[lang.code]);

      wsRef.current.on("ready", () => {
        setIsPlaying(false);
        wsRef.current.stop();
        setLoading(false);
      });

      wsRef.current.on("finish", () => {
        setIsPlaying(false);
        wsRef.current.stop();
      });
    } else if (wsRef.current && item.content[lang.code]) {
      // in this case we're just switching languages and there's already an audio source tied to the new language
      setLoading(true);
      wsRef.current.load(item.content[lang.code]);
    }

    if (wsRef.current && item.content[lang.code]?.length < 1) wsRef.current.destroy();
  }, [item, lang]);

  useEffect(() => {
    if (showMoreOptions) setTimeout(() => window.addEventListener("click", handleClick), 10);
    else window.removeEventListener("click", handleClick);
  }, [showMoreOptions]);

  const handleClick = useCallback((e) => {
    if (!optionsWindowRef.current?.contains(e.target)) setShowMoreOptions(false);
  }, []);

  useEffect(() => {
    if (editing) sourceTextRef.current.value = item.content[lang.code] || "";
  }, [editing]);

  function togglePlayPause() {
    setIsPlaying((p) => !p);
    wsRef.current.playPause();
  }

  function stopPlaying() {
    setIsPlaying(false);
    wsRef.current.stop();
  }

  const convertTime = (time) => {
    let minutes = parseInt(time / 60);
    let seconds = time - minutes * 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds.toFixed(2)}`;
  };

  function changeSource() {
    let temp = item;
    temp.content[lang.code] = sourceTextRef.current.value;
    setItem({ ...temp });
  }

  // https://d24hdgytax3x03.cloudfront.net/148/CoachingPoints/EnglishAudiopehrhw.wav
  // https://d24hdgytax3x03.cloudfront.net/212/ChapterAudios/Paysage roumain-audio_nknoyv.mp3

  return (
    <div className="FW-content">
      {editing && (
        <div className="FW-content-properties">
          <div className="FW-content-source">
            <label>
              Source:{" "}
              <input
                ref={sourceTextRef}
                type="text"
                onKeyDown={(e) => {
                  if (e.key === "Enter") changeSource();
                }}
              />
            </label>
            <IconButton color="blueButton" size="small" onClick={() => changeSource()}>
              <MdCheck />
            </IconButton>
          </div>
          {/* <div style={{ position: "relative" }}>
            <button
              className="FW-content__more-options-button"
              aria-label="more options"
              onClick={() => setShowMoreOptions(true)}
            >
              <svg viewBox="-1 -1 26 26" fill="#000">
                <circle cx="12" cy="2.611" r="2.611" />
                <circle cx="12" cy="12" r="2.611" />
                <circle cx="12" cy="21.389" r="2.611" />
              </svg>
            </button>
            {showMoreOptions && (
              <div ref={optionsWindowRef} className="FW-content__more-options-container">
                <div>
                  <label>
                    <input type="checkbox" checked={item.options?.autoPlay} onChange={toggleAutoplay} />
                    Autoplay
                  </label>
                </div>
              </div>
            )}
          </div> */}
        </div>
      )}
      <div
        className={`FW__audio-container ${item.content[lang.code] ? "" : "hidden"} ${
          leftPadding ? "left-padding" : ""
        }`}
      >
        {loading && <div className="FW__audio-loading-indicator">Loading...</div>}
        <div ref={wsContainer} className="FW__ws-wrapper" />
        <div className="FW__audio-bottom-bar">
          <div className="FW__ws-controls">
            <button onClick={togglePlayPause}>
              {!isPlaying ? (
                <svg viewBox="0 0 24 24">
                  <path d="M5.08,4.91V19.09a1.15,1.15,0,0,0,1.72.84l13.58-7a1,1,0,0,0,0-1.78L6.8,4.07A1.15,1.15,0,0,0,5.08,4.91Z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24">
                  <path d="M9,21H5a1,1,0,0,1-1-1V4A1,1,0,0,1,5,3H9a1,1,0,0,1,1,1V20A1,1,0,0,1,9,21ZM19,3H15a1,1,0,0,0-1,1V20a1,1,0,0,0,1,1h4a1,1,0,0,0,1-1V4A1,1,0,0,0,19,3Z" />
                </svg>
              )}
            </button>
            <button onClick={stopPlaying}>
              <svg viewBox="0 0 24 24">
                <path d="M19.087 20.087H4.913c-.552 0-1-.448-1-1V4.913c0-.552.448-1 1-1h14.175c.552 0 1 .448 1 1v14.175c-.001.552-.448.999-1.001.999z" />
              </svg>
            </button>
            <button onClick={() => wsRef.current.seekTo(0)}>
              <svg viewBox="0 0 24 24">
                <path d="M5.616 20.704H3.23c-.329 0-.597-.25-.597-.559V3.855c0-.309.267-.559.597-.559h2.386c.329 0 .597.25.597.559v16.291c-.001.308-.268.558-.597.558zM20.023 19.902 8.714 12.74c-.543-.344-.543-1.136 0-1.48l11.308-7.162c.583-.369 1.344.05 1.344.74v14.324c.001.69-.76 1.109-1.343.74z" />
              </svg>
            </button>
          </div>
          {!loading && <span>Length: {convertTime(wsRef.current?.getDuration())}</span>}
        </div>
      </div>
      {!item.content[lang.code] && <div className="FW-content-missing-notification">[No audio]</div>}
    </div>
  );
}

export default AudioItem;
